import { ApolloError } from "@apollo/client";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { AnalyticsResultDto, Field, GetUserSettingsQuery, MdmDeviceDto, MdmObjectJobDto, MdmProjectConfiguration, SortDirection } from "gql/generated/types";
import { ColumnFilterElementTemplateOptions } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import React from "react";
import { FieldErrors, FieldValues } from "react-hook-form";
import DeviceStatusTag from "../components/DeviceStatus";
import { priorityType } from "./const";
import { MdmTableName } from "./types";

  export const getFormErrorMessage = (name: keyof FieldValues, errors: FieldErrors<FieldValues>) => {
    return errors[name] ? <small className="p-error">{errors[name]?.message as string}</small> : <small className="p-error">&nbsp;</small>;
  }

  export const successToaster = (msg: string) => easasAlert({
    severity: "success",
    summary: "Success",
    detail: msg,
    life: 2000,
  });

  export const errorToaster = (err: ApolloError) => {
    easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
  };

  export const severityRowFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
    return (
      <Dropdown
        value={options.value}
        options={Object.entries(priorityType).map(([key, value]) => ({ label: value.value, value: key }))}
        onChange={e => options.filterApplyCallback(e.value)}
        placeholder="Select One"
        showClear
      />
    );
  };
  
  export const renderSeverity = (rowData: MdmObjectJobDto | AnalyticsResultDto) => (
    <i
      className={priorityType[rowData.severity!].icon}
      style={{ color: priorityType[rowData.severity!].severity }}
      title={priorityType[rowData.severity!].value}
    ><span className="pl-2 text-gray-900">{priorityType[rowData.severity!].value}</span></i>
  );

  export const handleDeviceStatus = (rowData?: MdmDeviceDto | null) => (
    <DeviceStatusTag
      status={rowData?.status.status}
      lastMeasurementDate={rowData?.status.lastMeasurementDate}
    />
  );

  export const rowDropdownFilterTemplate = (options: ColumnFilterElementTemplateOptions, dropdownOptions: any[] ) => {
    return (
      <Dropdown
        value={options.value}
        options={dropdownOptions}
        onChange={e => options.filterApplyCallback(e.value)}
        placeholder="Select One"
        showClear
      />
    );
  };

  type NestedField = Field & { children?: NestedField[]; data: Field[]; property: string };

  export const buildNestedFields = (
    containerType: string,
    fields: Field[] = [],
    key: string = "",
    data: Field[] = []
  ): NestedField[] => {
    return fields
    .filter(field => field.containerType === containerType)
    .map((field: Field, i) => {
        const k = key === "" ? `${i}` : `${key}-${i}`
        if (field.leaf) {
          return { ...field, data: [...data, field], key: k, property: field.key };
        }
        return {
          ...field,
          property: field.key,
          data: [...data, field],
          key: k,
          children: buildNestedFields(field.fieldType!, fields, k, [...data, field]),
        };
      });
  };

  export const findByKey = (data: NestedField[], key: string): NestedField | null => {
    // eslint-disable-next-line no-restricted-syntax
    for (const field of data) {
      if (field.key === key) {
        return field;
      }
      if (field.children && field.children.length > 0) {
        const found = findByKey(field.children, key);
        if (found) return found;
      }
    };
    return null;
  };

  export const userTableDetails = (userSettingsQuery: GetUserSettingsQuery | undefined, tableName: MdmTableName) =>
    userSettingsQuery?.userSettings.tablesDetails?.find(r => r?.table === tableName);


  export const formatSort = (
    sortField: string,
    sortOrder: number | undefined | null,
    tableName: MdmTableName,
    config: MdmProjectConfiguration | undefined | null
  ) => {
    let property = sortField!;
    const properties = sortField.split(".");
    if (tableName === "Devices" || tableName === "Objects") {
      property =
        properties.length === 2 && properties.at(-1) === "value"
          ? `customFields.${properties[0]}`
          : sortField!;
    }
    if (
      tableName.startsWith("Analytics") &&
      properties.length === 2 &&
      properties.at(-1) === "value"
    ) {
      if (config?.deviceConfiguration.customFields.find(f => f?.id === properties[0])) {
        property = `device.customFields.${properties[0]}`;
      }
      if (config?.objectConfiguration.customFields.find(f => f?.id === properties[0])) {
        property = `mdmObject.customFields.${properties[0]}`;
      }
    }
    return [{ property, direction: sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc }];
  };